import {
  StudioComponentDefinition,
  StudioComponentSource,
  StudioInsertPreset,
  StudioNode,
  StudioNodeStyle,
  buildStudioNodeStyles,
  createDefaultStudioNodeStyle,
  createStudioId
} from './component-studio.models';
import { PageRevisionSummary, WebsiteDataCollection, WebsitePageSummary, WebsiteSummary } from './website-management.models';

export type WebsiteBuilderNodeType = 'frame' | 'stack' | 'section' | 'text' | 'button' | 'media' | 'commerce' | 'badge';
export type WebsiteBuilderLinkKind = 'none' | 'external-url' | 'internal-page';
export type WebsiteBuilderHoverEffect = 'none' | 'lift' | 'grow' | 'glow';
export type WebsiteBuilderNodeRole = 'page-root' | 'section' | 'component-root' | 'element';
export type WebsiteBuilderStyleMode = 'light' | 'dark';
export type WebsiteBuilderStyleVariants = Record<string, Partial<Record<WebsiteBuilderStyleMode, Partial<StudioNodeStyle>>>>;
export type WebsiteBuilderDynamicComponentKind =
  | 'none'
  | 'collection-list'
  | 'catalog-grid'
  | 'filter-panel'
  | 'storefront-runtime';

export interface WebsiteBuilderNodeContent {
  translationKey?: string;
  eyebrow?: string;
  title?: string;
  text?: string;
  secondaryText?: string;
  buttonText?: string;
  badgeText?: string;
  mediaLabel?: string;
  caption?: string;
  mediaUrl?: string;
  mediaAlt?: string;
  commerceTemplate?: string;
  commerceWidget?: string;
  commerceSlot?: string;
  commerceBinding?: string;
  commerceAction?: string;
  dynamicComponent?: WebsiteBuilderDynamicComponentKind;
  dataCollectionKey?: string;
  dataItemAlias?: string;
  dataItemLimit?: number;
  dataBinding?: string;
  dataAction?: string;
  repeatTemplateKey?: string;
  repeatPartKey?: string;
  href?: string;
  linkKind?: WebsiteBuilderLinkKind;
  targetPagePublicId?: string | null;
  openInNewTab?: boolean;
  hoverEffect?: WebsiteBuilderHoverEffect;
}

export interface WebsiteBuilderNode {
  id: string;
  type: WebsiteBuilderNodeType;
  label: string;
  hidden: boolean;
  locked: boolean;
  nodeRole: WebsiteBuilderNodeRole;
  originComponentId: string | null;
  originComponentName: string | null;
  originComponentSource: StudioComponentSource | null;
  content: WebsiteBuilderNodeContent;
  style: StudioNodeStyle;
  styleVariants: WebsiteBuilderStyleVariants;
  children: WebsiteBuilderNode[];
}

export interface WebsitePageDocument {
  id: string;
  type: 'website-page-document';
  websitePublicId: string | null;
  pagePublicId: string | null;
  title: string;
  description: string;
  createdAt: string;
  updatedAt: string;
  root: WebsiteBuilderNode;
}

export interface WebsiteBuilderDraftState {
  website: WebsiteSummary;
  page: WebsitePageSummary;
  pages: WebsitePageSummary[];
  dataCollections: WebsiteDataCollection[];
  draftRevision: PageRevisionSummary | null;
  document: WebsitePageDocument;
}

export const WEBSITE_BUILDER_SECTION_PRESETS: StudioInsertPreset[] = [
  {
    id: 'hero-section',
    label: 'Hero section',
    description: 'Section wrapper with lead copy and primary call-to-action controls.',
    accent: '#2563eb',
    icon: 'terminal-outline'
  },
  {
    id: 'content-band',
    label: 'Content band',
    description: 'Section with heading and longer supporting copy for mid-page content.',
    accent: '#0f766e',
    icon: 'document-text-outline'
  },
  {
    id: 'feature-grid',
    label: 'Feature grid',
    description: 'Section starter with stacked feature cards inside a layout container.',
    accent: '#7c3aed',
    icon: 'grid-outline'
  },
  {
    id: 'cta-section',
    label: 'CTA section',
    description: 'Section focused on one conversion action with concise supporting text.',
    accent: '#ea580c',
    icon: 'megaphone-outline'
  },
  {
    id: 'testimonial-band',
    label: 'Testimonial band',
    description: 'Section starter for trust copy, quotes, and social proof rows.',
    accent: '#ca8a04',
    icon: 'chatbubbles-outline'
  }
];

export function createWebsiteBuilderId(prefix: string): string {
  return createStudioId(prefix);
}

export function isWebsiteBuilderContainerNode(node: WebsiteBuilderNode | null | undefined): boolean {
  return !!node && (node.type === 'frame' || node.type === 'stack' || node.type === 'section');
}

export function websiteBuilderDropListId(nodeId: string): string {
  return `website-builder-drop-${nodeId}`;
}

export function collectWebsiteBuilderDropListIds(root: WebsiteBuilderNode): string[] {
  const ids: string[] = [];

  walkWebsiteBuilderNodes(root, (node) => {
    if (isWebsiteBuilderContainerNode(node)) {
      ids.push(websiteBuilderDropListId(node.id));
    }
  });

  return ids;
}

export function walkWebsiteBuilderNodes(node: WebsiteBuilderNode, visitor: (node: WebsiteBuilderNode) => void): void {
  visitor(node);
  node.children.forEach((child) => walkWebsiteBuilderNodes(child, visitor));
}

export function findWebsiteBuilderNode(root: WebsiteBuilderNode, nodeId: string): WebsiteBuilderNode | null {
  if (root.id === nodeId) {
    return root;
  }

  for (const child of root.children) {
    const match = findWebsiteBuilderNode(child, nodeId);
    if (match) {
      return match;
    }
  }

  return null;
}

export function findWebsiteBuilderParentNode(root: WebsiteBuilderNode, nodeId: string): WebsiteBuilderNode | null {
  for (const child of root.children) {
    if (child.id === nodeId) {
      return root;
    }

    const match = findWebsiteBuilderParentNode(child, nodeId);
    if (match) {
      return match;
    }
  }

  return null;
}

export function buildWebsiteBuilderNodeStyles(
  node: WebsiteBuilderNode,
  packageId?: string,
  mode?: WebsiteBuilderStyleMode
): Record<string, string | number> {
  const styles = buildStudioNodeStyles({
    ...(node as unknown as StudioNode),
    style: resolveWebsiteBuilderNodeStyle(node, packageId, mode)
  });

  return normalizeRenderedWebsiteBuilderNodeStyles(node, styles);
}

export function normalizeRenderedWebsiteBuilderNodeStyles(
  node: WebsiteBuilderNode,
  styles: Record<string, string | number>
): Record<string, string | number> {
  const next = { ...styles };
  const isRoot = node.nodeRole === 'page-root';
  const isStructuralNode = node.type === 'frame' || node.type === 'section' || node.type === 'stack';
  const position = typeof next['position'] === 'string' ? next['position'].trim().toLowerCase() : 'relative';
  const width = typeof next['width'] === 'string' ? next['width'].trim().toLowerCase() : '';

  if (typeof next['color'] === 'string' && next['color'].trim().toLowerCase() === 'var(--bw-canvas-text-color)') {
    next['color'] = 'inherit';
  }

  if (node.type === 'media' && typeof next['object-fit'] === 'string' && next['object-fit'].trim().toLowerCase() === 'fill') {
    next['object-fit'] = 'cover';
  }

  if (position !== 'absolute' && position !== 'fixed' && position !== 'sticky') {
    next['position'] = 'relative';
  }

  if (isStructuralNode && width !== 'auto') {
    next['min-width'] = '0';
    const maxWidth = typeof next['max-width'] === 'string' ? next['max-width'].trim().toLowerCase() : '';
    if (!maxWidth || maxWidth === 'auto' || maxWidth === 'none') {
      next['max-width'] = '100%';
    }
  }

  if (isRoot) {
    next['position'] = 'relative';
    next['width'] = '100%';
    next['max-width'] = '100%';
    next['min-width'] = '0';
    next['height'] = '100%';
    next['min-height'] = '100%';
  }

  return next;
}

export function resolveWebsiteBuilderNodeStyle(
  node: WebsiteBuilderNode,
  packageId?: string,
  mode?: WebsiteBuilderStyleMode
): StudioNodeStyle {
  const baseStyle = normalizeVariantMergedStyle(node.style);
  const variantPatch = packageId && mode ? node.styleVariants?.[packageId]?.[mode] : null;

  if (!variantPatch) {
    return baseStyle;
  }

  return normalizeVariantMergedStyle({
    ...baseStyle,
    ...variantPatch,
    transform: {
      ...baseStyle.transform,
      ...(variantPatch.transform ?? {})
    },
    animation: {
      ...baseStyle.animation,
      ...(variantPatch.animation ?? {})
    }
  });
}

export function normalizeWebsiteBuilderNodeTree(node: WebsiteBuilderNode): WebsiteBuilderNode {
  const normalizedBaseStyle = normalizeFluidWebsiteNodeStyle(
    {
      ...createDefaultStudioNodeStyle(),
      ...node.style,
      transform: {
        ...createDefaultStudioNodeStyle().transform,
        ...(node.style?.transform ?? {})
      },
      animation: {
        ...createDefaultStudioNodeStyle().animation,
        ...(node.style?.animation ?? {})
      }
    },
    node.type,
    node.nodeRole,
    node.nodeRole === 'page-root'
  );

  return {
    ...node,
    style: normalizedBaseStyle,
    styleVariants: normalizeWebsiteBuilderStyleVariants(node.styleVariants),
    children: node.children.map((child) => normalizeWebsiteBuilderNodeTree(child))
  };
}

export function normalizeWebsitePageDocument(document: WebsitePageDocument): WebsitePageDocument {
  return {
    ...document,
    root: normalizeWebsiteBuilderNodeTree(document.root)
  };
}

function normalizeVariantMergedStyle(style: Partial<StudioNodeStyle> | null | undefined): StudioNodeStyle {
  const defaults = createDefaultStudioNodeStyle();
  return {
    ...defaults,
    ...(style ?? {}),
    transform: {
      ...defaults.transform,
      ...(style?.transform ?? {})
    },
    animation: {
      ...defaults.animation,
      ...(style?.animation ?? {})
    }
  };
}

function normalizeFluidWebsiteNodeStyle(
  style: StudioNodeStyle,
  nodeType: WebsiteBuilderNodeType,
  nodeRole: WebsiteBuilderNodeRole,
  isRootNode: boolean
): StudioNodeStyle {
  const next: StudioNodeStyle = { ...style };
  const position = next.position.toLowerCase();
  const isStructuralNode = nodeType === 'frame' || nodeType === 'section' || nodeType === 'stack';
  const isRoot = isRootNode || nodeRole === 'page-root';

  if (next.color.trim().toLowerCase() === 'var(--bw-canvas-text-color)') {
    next.color = 'inherit';
  }

  if (nodeType === 'media' && next.objectFit.trim().toLowerCase() === 'fill') {
    next.objectFit = 'cover';
  }

  if (position !== 'absolute' && position !== 'fixed' && position !== 'sticky') {
    next.position = 'relative';
  }

  if (isStructuralNode && next.width.trim().toLowerCase() !== 'auto') {
    next.minWidth = '0px';
    const maxWidth = next.maxWidth.trim().toLowerCase();
    if (!maxWidth || maxWidth === 'auto' || maxWidth === 'none') {
      next.maxWidth = '100%';
    }
  }

  if (isRoot) {
    next.position = 'relative';
    next.width = '100%';
    next.maxWidth = '100%';
    next.minWidth = '0px';
    next.height = '100%';
    next.minHeight = '100%';
  }

  return next;
}

function normalizeWebsiteBuilderStyleVariants(input: unknown): WebsiteBuilderStyleVariants {
  if (!input || typeof input !== 'object' || Array.isArray(input)) {
    return {};
  }

  return Object.entries(input as Record<string, unknown>).reduce<WebsiteBuilderStyleVariants>((accumulator, [packageId, value]) => {
    if (!packageId || !value || typeof value !== 'object' || Array.isArray(value)) {
      return accumulator;
    }

    const modeRecord = value as Partial<Record<WebsiteBuilderStyleMode, Partial<StudioNodeStyle>>>;
    const nextModes: Partial<Record<WebsiteBuilderStyleMode, Partial<StudioNodeStyle>>> = {};

    for (const mode of ['light', 'dark'] as const) {
      const stylePatch = modeRecord[mode];
      if (stylePatch && typeof stylePatch === 'object' && !Array.isArray(stylePatch)) {
        nextModes[mode] = structuredClone(stylePatch);
      }
    }

    if (Object.keys(nextModes).length > 0) {
      accumulator[packageId] = nextModes;
    }

    return accumulator;
  }, {});
}

export function getWebsiteBuilderNodeIcon(nodeType: WebsiteBuilderNodeType): string {
  switch (nodeType) {
    case 'section':
      return 'albums-outline';
    case 'frame':
      return 'scan-outline';
    case 'stack':
      return 'layers-outline';
    case 'text':
      return 'text-outline';
    case 'button':
      return 'radio-button-on-outline';
    case 'media':
      return 'image-outline';
    case 'commerce':
      return 'cube-outline';
    case 'badge':
      return 'pricetag-outline';
    default:
      return 'shapes-outline';
  }
}

export function cloneComponentDefinitionIntoPageNode(definition: StudioComponentDefinition): WebsiteBuilderNode {
  const rootNode = cloneStudioNodeIntoPageNode(definition.root, true);
  rootNode.nodeRole = 'component-root';
  rootNode.originComponentId = definition.id;
  rootNode.originComponentName = definition.name;
  rootNode.originComponentSource = definition.source;
  rootNode.label = definition.name;

  return rootNode;
}

export function createComponentDefinitionFromPageNode(
  node: WebsiteBuilderNode,
  componentName: string
): StudioComponentDefinition {
  const normalizedName = componentName.trim() || node.label || 'Untitled component';
  const timestamp = new Date().toISOString();

  return {
    id: createStudioId('component'),
    slug: slugify(normalizedName),
    name: normalizedName,
    source: 'custom',
    category: inferComponentCategory(node),
    description: `Saved from the website builder using the "${node.label}" selection.`,
    tags: ['page-saved'],
    version: 1,
    status: 'draft',
    usageCount: 0,
    updatedAt: timestamp,
    root: createFrameRootFromPageNode(node)
  };
}

export function createWebsiteBuilderSectionPreset(presetId: string): WebsiteBuilderNode {
  switch (presetId) {
    case 'feature-grid':
      return createSectionNode('Feature grid', [
        createTextNode('Feature grid intro', {
          eyebrow: 'Feature grid',
          title: 'Summarize the strongest reasons to choose this offer.',
          text: 'Use this section as a technical starter and replace the placeholder feature cards with your own content.'
        }),
        createStackNode('Feature card row', {
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: '1rem'
        }, [
          createMediaNode('Feature card 1', {
            mediaLabel: 'Feature 01',
            caption: 'Describe the value of the first feature.'
          }),
          createMediaNode('Feature card 2', {
            mediaLabel: 'Feature 02',
            caption: 'Describe the value of the second feature.'
          }),
          createMediaNode('Feature card 3', {
            mediaLabel: 'Feature 03',
            caption: 'Describe the value of the third feature.'
          })
        ])
      ]);
    case 'cta-section':
      return createSectionNode('CTA section', [
        createTextNode('CTA copy', {
          eyebrow: 'Conversion section',
          title: 'Tell visitors exactly what to do next.',
          text: 'Keep this section short and conversion-oriented. One clear outcome, one strong action.'
        }),
        createStackNode('CTA buttons', {
          flexDirection: 'row',
          gap: '0.75rem',
          alignItems: 'center'
        }, [
          createButtonNode('Primary CTA', {
            buttonText: 'Start now',
            linkKind: 'none'
          }, {
            backgroundColor: '#2563eb',
            color: '#ffffff',
            borderColor: '#2563eb'
          }),
          createButtonNode('Secondary CTA', {
            buttonText: 'Learn more',
            linkKind: 'none'
          }, {
            backgroundColor: 'rgba(15, 23, 42, 0.04)',
            borderColor: 'rgba(15, 23, 42, 0.12)'
          })
        ])
      ]);
    case 'testimonial-band':
      return createSectionNode('Testimonial band', [
        createTextNode('Trust copy', {
          eyebrow: 'Trust layer',
          title: 'Add a concise quote or social proof statement.',
          text: 'Use testimonials, review highlights, and logos to reduce hesitation.'
        }),
        createStackNode('Badge row', {
          flexDirection: 'row',
          gap: '0.65rem',
          flexWrap: 'wrap'
        }, [
          createBadgeNode('Review badge', { badgeText: '4.9/5' }),
          createBadgeNode('Project badge', { badgeText: '120+ launches' }),
          createBadgeNode('Retention badge', { badgeText: 'Long-term clients' })
        ])
      ]);
    case 'content-band':
      return createSectionNode('Content band', [
        createTextNode('Content intro', {
          eyebrow: 'Content section',
          title: 'Explain the business value of this part of the page.',
          text: 'Use a technical section starter, then customize copy, layout, and media blocks as needed.'
        })
      ]);
    case 'hero-section':
    default:
      return createSectionNode('Hero section', [
        createTextNode('Hero copy', {
          eyebrow: 'Hero section',
          title: 'Write a strong first-screen promise.',
          text: 'Keep the value proposition clear, specific, and tied to the action you want the visitor to take.',
          secondaryText: 'Optional supporting line for context, differentiation, or reassurance.'
        }),
        createStackNode('Hero CTAs', {
          flexDirection: 'row',
          gap: '0.75rem',
          alignItems: 'center'
        }, [
          createButtonNode('Primary CTA', {
            buttonText: 'Primary action',
            linkKind: 'none'
          }, {
            backgroundColor: '#2563eb',
            color: '#ffffff',
            borderColor: '#2563eb'
          }),
          createButtonNode('Secondary CTA', {
            buttonText: 'Secondary action',
            linkKind: 'none'
          }, {
            backgroundColor: 'rgba(15, 23, 42, 0.04)',
            borderColor: 'rgba(15, 23, 42, 0.12)'
          })
        ])
      ]);
  }
}

function createFrameRootFromPageNode(node: WebsiteBuilderNode): StudioNode {
  return {
    id: createStudioId('node'),
    type: 'frame',
    label: `${node.label} root`,
    hidden: false,
    locked: false,
    content: {},
    style: {
      ...createBaseStyle(),
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
      width: '100%',
      maxWidth: '100%',
      minHeight: '18rem',
      padding: '1.5rem',
      backgroundColor: '#ffffff',
      borderRadius: '1.5rem'
    },
    children: [clonePageNodeIntoStudioNode(node)]
  };
}

function cloneStudioNodeIntoPageNode(node: StudioNode, isRoot = false): WebsiteBuilderNode {
  return {
    id: createWebsiteBuilderId('node'),
    type: normalizePageNodeType(node.type),
    label: node.label,
    hidden: node.hidden,
    locked: node.locked,
    nodeRole: isRoot ? 'component-root' : 'element',
    originComponentId: null,
    originComponentName: null,
    originComponentSource: null,
    content: {
      translationKey: undefined,
      eyebrow: node.content.eyebrow,
      title: node.content.title,
      text: node.content.text,
      secondaryText: node.content.secondaryText,
      buttonText: node.content.buttonText,
      badgeText: node.content.badgeText,
      mediaLabel: node.content.mediaLabel,
      caption: node.content.caption,
      mediaUrl: node.content.mediaUrl,
      mediaAlt: node.content.mediaAlt,
      href: node.content.href,
      linkKind: node.content.href ? 'external-url' : 'none',
      targetPagePublicId: null,
      openInNewTab: false,
      hoverEffect: 'none'
    },
    style: {
      ...createDefaultStudioNodeStyle(),
      ...structuredClone(node.style),
      transform: {
        ...createDefaultStudioNodeStyle().transform,
        ...(structuredClone(node.style.transform) ?? {})
      },
      animation: {
        ...createDefaultStudioNodeStyle().animation,
        ...(structuredClone(node.style.animation) ?? {})
      }
    },
    styleVariants: {},
    children: node.children.map((child) => cloneStudioNodeIntoPageNode(child))
  };
}

function clonePageNodeIntoStudioNode(node: WebsiteBuilderNode): StudioNode {
  return {
    id: createStudioId('node'),
    type: normalizeStudioNodeType(node.type),
    label: node.label,
    hidden: node.hidden,
    locked: node.locked,
    content: {
      eyebrow: node.content.eyebrow,
      title: node.content.title,
      text: node.content.text,
      secondaryText: node.content.secondaryText,
      buttonText: node.content.buttonText,
      badgeText: node.content.badgeText,
      mediaLabel: node.content.mediaLabel,
      caption: node.content.caption,
      mediaUrl: node.content.mediaUrl,
      mediaAlt: node.content.mediaAlt,
      href: node.content.linkKind === 'external-url' ? (node.content.href || '') : ''
    },
    style: {
      ...createDefaultStudioNodeStyle(),
      ...structuredClone(node.style),
      transform: {
        ...createDefaultStudioNodeStyle().transform,
        ...(structuredClone(node.style.transform) ?? {})
      },
      animation: {
        ...createDefaultStudioNodeStyle().animation,
        ...(structuredClone(node.style.animation) ?? {})
      }
    },
    children: node.children.map((child) => clonePageNodeIntoStudioNode(child))
  };
}

function normalizePageNodeType(nodeType: string): WebsiteBuilderNodeType {
  return nodeType === 'frame'
    || nodeType === 'stack'
    || nodeType === 'text'
    || nodeType === 'button'
    || nodeType === 'media'
    || nodeType === 'commerce'
    || nodeType === 'badge'
    ? nodeType
    : 'stack';
}

function normalizeStudioNodeType(nodeType: WebsiteBuilderNodeType): StudioNode['type'] {
  if (nodeType === 'commerce') {
    return 'media';
  }

  return nodeType === 'section' ? 'frame' : nodeType;
}

function inferComponentCategory(node: WebsiteBuilderNode): StudioComponentDefinition['category'] {
  if (node.originComponentId) {
    return 'utility';
  }

  if (node.type === 'button' || node.type === 'badge') {
    return 'utility';
  }

  if (node.type === 'media' || node.type === 'commerce') {
    return 'content';
  }

  return 'marketing';
}

function createSectionNode(label: string, children: WebsiteBuilderNode[] = []): WebsiteBuilderNode {
  return {
    id: createWebsiteBuilderId('node'),
    type: 'section',
    label,
    hidden: false,
    locked: false,
    nodeRole: 'section',
    originComponentId: null,
    originComponentName: null,
    originComponentSource: null,
    content: {},
    style: {
      ...createBaseStyle(),
      alignItems: 'center',
      width: '100%',
      maxWidth: '100%',
      minHeight: '14rem',
      padding: '1.5rem',
      borderRadius: '1.5rem',
      boxShadow: '0 1rem 2rem rgba(15, 23, 42, 0.06)'
    },
    styleVariants: {},
    children
  };
}

function createStackNode(
  label: string,
  stylePatch: Partial<StudioNodeStyle> = {},
  children: WebsiteBuilderNode[] = []
): WebsiteBuilderNode {
  return {
    id: createWebsiteBuilderId('node'),
    type: 'stack',
    label,
    hidden: false,
    locked: false,
    nodeRole: 'element',
    originComponentId: null,
    originComponentName: null,
    originComponentSource: null,
    content: {},
    style: {
      ...createBaseStyle(),
      padding: '0',
      borderWidth: '0',
      backgroundColor: 'transparent',
      boxShadow: 'none',
      width: '100%',
      maxWidth: '100%',
      ...stylePatch
    },
    styleVariants: {},
    children
  };
}

function createTextNode(
  label: string,
  content: WebsiteBuilderNodeContent,
  stylePatch: Partial<StudioNodeStyle> = {}
): WebsiteBuilderNode {
  return {
    id: createWebsiteBuilderId('node'),
    type: 'text',
    label,
    hidden: false,
    locked: false,
    nodeRole: 'element',
    originComponentId: null,
    originComponentName: null,
    originComponentSource: null,
    content,
    style: {
      ...createBaseStyle(),
      gap: '0.5rem',
      padding: '0',
      backgroundColor: 'transparent',
      borderWidth: '0',
      boxShadow: 'none',
      ...stylePatch
    },
    styleVariants: {},
    children: []
  };
}

function createButtonNode(
  label: string,
  content: WebsiteBuilderNodeContent,
  stylePatch: Partial<StudioNodeStyle> = {}
): WebsiteBuilderNode {
  return {
    id: createWebsiteBuilderId('node'),
    type: 'button',
    label,
    hidden: false,
    locked: false,
    nodeRole: 'element',
    originComponentId: null,
    originComponentName: null,
    originComponentSource: null,
    content,
    style: {
      ...createBaseStyle(),
      display: 'inline-flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: 'auto',
      padding: '0.85rem 1.2rem',
      borderRadius: '999px',
      fontWeight: '700',
      ...stylePatch
    },
    styleVariants: {},
    children: []
  };
}

function createMediaNode(
  label: string,
  content: WebsiteBuilderNodeContent,
  stylePatch: Partial<StudioNodeStyle> = {}
): WebsiteBuilderNode {
  return {
    id: createWebsiteBuilderId('node'),
    type: 'media',
    label,
    hidden: false,
    locked: false,
    nodeRole: 'element',
    originComponentId: null,
    originComponentName: null,
    originComponentSource: null,
    content,
    style: {
      ...createBaseStyle(),
      justifyContent: 'space-between',
      alignItems: 'flex-end',
      minHeight: '14rem',
      padding: '1.15rem',
      backgroundColor: 'rgba(15, 23, 42, 0.06)',
      borderRadius: '1.25rem',
      ...stylePatch
    },
    styleVariants: {},
    children: []
  };
}

function createBadgeNode(
  label: string,
  content: WebsiteBuilderNodeContent,
  stylePatch: Partial<StudioNodeStyle> = {}
): WebsiteBuilderNode {
  return {
    id: createWebsiteBuilderId('node'),
    type: 'badge',
    label,
    hidden: false,
    locked: false,
    nodeRole: 'element',
    originComponentId: null,
    originComponentName: null,
    originComponentSource: null,
    content,
    style: {
      ...createBaseStyle(),
      display: 'inline-flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: 'auto',
      padding: '0.45rem 0.8rem',
      borderRadius: '999px',
      backgroundColor: 'rgba(37, 99, 235, 0.12)',
      color: '#1d4ed8',
      borderColor: 'rgba(37, 99, 235, 0.18)',
      borderWidth: '1px',
      fontSize: '0.82rem',
      fontWeight: '700',
      ...stylePatch
    },
    styleVariants: {},
    children: []
  };
}

function createBaseStyle(): StudioNodeStyle {
  return createDefaultStudioNodeStyle();
}

function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'component';
}
